import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trophy, Info } from "lucide-react";

interface PrizeInfoOverlayProps {
  maxPlayers: number;
  currentPlayers: number;
}

export const PrizeInfoOverlay = ({ maxPlayers, currentPlayers }: PrizeInfoOverlayProps) => {
  const { t } = useTranslation();

  // Prize structure is based on maxPlayers (total seats), not current count
  const prizes = useMemo(() => {
    if (maxPlayers <= 4) return [{ pos: 1, percent: 100 }];
    if (maxPlayers <= 6) return [{ pos: 1, percent: 70 }, { pos: 2, percent: 30 }];
    return [
      { pos: 1, percent: 55 },
      { pos: 2, percent: 30 },
      { pos: 3, percent: 15 },
    ];
  }, [maxPlayers]);

  const getOrdinal = (n: number) => {
    if (n === 1) return "1st";
    if (n === 2) return "2nd";
    if (n === 3) return "3rd";
    return `${n}th`;
  };

  const getTrophyColor = (pos: number) => {
    if (pos === 1) return "text-yellow-400";
    if (pos === 2) return "text-gray-300";
    if (pos === 3) return "text-amber-600";
    return "text-primary";
  };

  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center p-4 pointer-events-none">
      <Card className="w-full max-w-xs border-primary/20 bg-background/90 shadow-2xl backdrop-blur supports-[backdrop-filter]:bg-background/60 pointer-events-auto">
        <CardHeader className="text-center pb-2">
          <CardTitle className="flex items-center justify-center gap-2 text-xl">
            <Trophy className="h-6 w-6 text-primary" />
            <span>{t('prize_title')}</span>
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {t('prize_players')}: <span className="font-bold text-foreground">{currentPlayers}/{maxPlayers}</span>
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3 rounded-lg bg-secondary/20 p-4">
            {prizes.map((prize) => (
              <div
                key={prize.pos}
                className="flex items-center justify-between border-b border-border/50 pb-2 last:border-0 last:pb-0"
              >
                <div className="flex items-center gap-3">
                  <div className={`font-bold ${getTrophyColor(prize.pos)} w-8 text-center`}>
                    {getOrdinal(prize.pos)}
                  </div>
                  <span className="text-sm font-medium text-foreground/80">{t('prize_place')}</span>
                </div>
                <div className="font-mono text-lg font-bold text-primary">
                  {prize.percent}%
                </div>
              </div>
            ))}
            {currentPlayers < 2 && (
              <div className="flex items-center justify-center gap-2 py-2 text-xs text-muted-foreground">
                <Info className="h-3 w-3" />
                <span>{t('prize_waiting')}</span>
              </div>
            )}
          </div>

        </CardContent>
      </Card>
    </div>
  );
};
